import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyListLayout,
  normalizeImportItems,
} from './importListLayout';
import {
  applyPoolLists,
  prettyImportName,
  poolListsLabel,
  resolvePoolLists,
  togglePoolList,
} from './contactPoolLists';

const matchesQuery = (name, q) => {
  if (!q) return true;
  return String(name || '').toLowerCase().includes(q);
};

const Check = ({ on }) => (
  <span className={`oj-listpick-check${on ? ' on' : ''}`} aria-hidden="true">
    {on && (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )}
  </span>
);

const OutreachImportListPicker = ({
  pool,
  onChange,
  importOptions = [],
  layout,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const resolved = resolvePoolLists(pool);
  const selected = new Set(resolved.names);
  const q = search.trim().toLowerCase();

  const items = useMemo(() => normalizeImportItems(importOptions), [importOptions]);
  const { visible, hidden } = useMemo(
    () => applyListLayout(items, layout),
    [items, layout],
  );

  const visibleShown = visible.filter((i) => matchesQuery(i.name, q));
  const hiddenShown = hidden.filter((i) => matchesQuery(i.name, q));

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setHiddenOpen(false);
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const setMode = (mode) => onChange(applyPoolLists(pool, { mode }));

  const renderItem = (item) => {
    const on = selected.has(item.name);
    return (
      <button
        key={item.name}
        type="button"
        className={`oj-listpick-item${on ? ' selected' : ''}`}
        onClick={() => onChange(togglePoolList(pool, item.name))}
      >
        <Check on={on} />
        <span className="oj-listpick-item-body">
          <span className="oj-listpick-item-name">{item.name}</span>
          {item.imported_count != null && (
            <span className="oj-listpick-item-meta">{item.imported_count} contacts</span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div className="oj-listpick" ref={wrapRef}>
      <div className="oj-listpick-anchor">
        <button
        type="button"
        className={`oj-listpick-trigger${open ? ' open' : ''}${resolved.mode !== 'any' ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        >
        <span className="oj-listpick-trigger-label">{poolListsLabel(pool)}</span>
        <svg className={`oj-listpick-chevron${open ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="oj-listpick-panel" role="listbox" aria-multiselectable="true">
          <div className="oj-listpick-search">
            <i className="fa fa-search" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lists"
              aria-label="Search lists"
            />
          </div>

          <button
            type="button"
            className={`oj-listpick-item oj-listpick-special${resolved.mode === 'any' ? ' selected' : ''}`}
            onClick={() => { setMode('any'); setOpen(false); }}
          >
            <span className={`oj-listpick-radio${resolved.mode === 'any' ? ' on' : ''}`} />
            <span className="oj-listpick-item-body">
              <span className="oj-listpick-item-name">Any list</span>
              <span className="oj-listpick-item-meta">{items.length} lists</span>
            </span>
          </button>
          <button
            type="button"
            className={`oj-listpick-item oj-listpick-special${resolved.mode === 'manual' ? ' selected' : ''}`}
            onClick={() => { setMode('manual'); setOpen(false); }}
          >
            <span className={`oj-listpick-radio${resolved.mode === 'manual' ? ' on' : ''}`} />
            <span className="oj-listpick-item-body">
              <span className="oj-listpick-item-name">Manual — add from the table</span>
            </span>
          </button>

          {(visibleShown.length > 0 || hiddenShown.length > 0 || hidden.length > 0) && (
            <div className="oj-listpick-divider" />
          )}

          <div className="oj-listpick-list">
            {visibleShown.map(renderItem)}

            {hidden.length > 0 && (
              <>
                <button
                  type="button"
                  className={`oj-listpick-hidden${hiddenOpen ? ' open' : ''}`}
                  onClick={() => setHiddenOpen((v) => !v)}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  <span>Hidden</span>
                  <em>{hidden.length}</em>
                </button>
                {hiddenOpen && hiddenShown.map(renderItem)}
              </>
            )}

            {items.length === 0 && (
              <div className="oj-listpick-empty">No imports yet</div>
            )}
            {items.length > 0 && visibleShown.length === 0 && hiddenShown.length === 0 && q && (
              <div className="oj-listpick-empty">No lists match “{search.trim()}”</div>
            )}
          </div>
        </div>
      )}
      </div>

      {resolved.mode === 'lists' && resolved.names.length > 1 && (
        <div className="oj-listpick-chips">
          {resolved.names.map((name) => (
            <button
              key={name}
              type="button"
              className="oj-listpick-chip"
              onClick={() => onChange(togglePoolList(pool, name))}
              title={`Remove ${name}`}
            >
              <span>{prettyImportName(name) || name}</span>
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default OutreachImportListPicker;
